import React, {useContext, useEffect, useMemo, useState} from "react";
import {Table, TableBody, TableCell, TableHead, TableRow} from "@material-ui/core";
import AnnualReportTableCell from "../common/AnnualReportTableCell";
import FormTranslationContext from "context/FormTranslationContext";
import PropTypes from "prop-types";
import Tr from "translations/Translations";
import WorkableIconExpandButton from "components/button/WorkableIconExpandButton";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import SwapVerticalCircleIcon from '@material-ui/icons/SwapVerticalCircle';
import WorkableIconButton from "components/button/WorkableIconButton";
import {getMoveAnnualReportEntryConfig} from "../common/MoveAnnualReportEntry";
import WorkableIconErrorButton from "components/button/WorkableIconErrorButton";
import {useParams} from "react-router";
import useSnackbar from "hooks/useSnackbar";
import WorkableIconEditButton from "components/button/WorkableIconEditButton";
import {getAnnualReportEditPopup} from "../common/AnnualReportEditPopup";
import IconButton from "components/button/IconButton";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import {simpleConfirmNoTranslate} from "libs/ConfirmNotification";
import AnnualReportsService from "services/AnnualReportsService";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import clsx from "clsx";
import scrollTo from "libs/scrollTo";
import formatAmountNumber from "libs/formatAmountNumber";
import AnnualReportTableHeadCell from "../common/AnnualReportTableHeadCell";

function AnnualReportExceptionTablePart(
    {
        type,
        reportStep,
        classes,
        disabled,
        entries,
        index,
        search,
        setEditPopup,
        setMovePopup
    }
) {
    const params = useParams();
    const {dictTranslations} = useContext(FormTranslationContext)
    const [expanded, setExpanded] = useState(false);
    const [hasIncomplete, setHasIncomplete] = useState(false);
    const {enqueueSuccessSnackbar} = useSnackbar();

    const [entriesCounted, setEntriesCounted] = useState({
        count: 0,
        contractValue: 0,
    })

    const entriesFilteres = useMemo(() => {
        if (search.search) {
            return entries.filter((entry) => entry.number.includes(search.search))
        }
        return entries;
    }, [entries, search])

    useEffect(() => {
        let incomplete = false;
        setEntriesCounted(entries.reduce((acc, entry) => {
            incomplete = incomplete || !entry.complete;
            if (entry.contractValue)
                acc.contractValue += parseFloat(entry.contractValue)
            acc.count++
            return acc;
        }, {count: 0, contractValue: 0}))
        setHasIncomplete(incomplete);
    }, [entries])

    useDidUpdateEffect(() => {
        if (!expanded && search.search && entries.some((entry) => entry.number.includes(search.search))) {
            setExpanded(true);
        }
    }, [search])

    const doDelete = (entry) => {
        simpleConfirmNoTranslate(Tr("confirm_delete_annual_entry", {number: entry.number}), () => {
            AnnualReportsService.deleteAnnualReportTableEntry(params.id, entry.identity, reportStep, type).then((result) => {
                if (result) {
                    enqueueSuccessSnackbar(Tr("deleted") + " " + entry.number)
                }
            })
        })
    }

    const changeComplete = (entry, complete) => {
        const data = {
            ...entry,
            complete
        }
        AnnualReportsService.saveAnnualReportTableEntry(params.id, reportStep, data)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        enqueueSuccessSnackbar(Tr("saved") + " " + entry.number)
                    } else {
                        setEditPopup(getAnnualReportEditPopup(entry, false, true))
                    }
                }

            })
    }

    return (<>
            <TableRow className={classes.variantRow}>
                <AnnualReportTableCell borderLeft>{index !== 0 ? index : null }</AnnualReportTableCell>
                <AnnualReportTableCell borderLeft onClick={() => setExpanded(!expanded)} className={classes.exceptionName}>
                    {`${dictTranslations.exceptions[type]} (${entriesFilteres.length}/${entries.length})`}
                </AnnualReportTableCell>
                <AnnualReportTableCell onClick={() => setExpanded(!expanded)}>
                    {hasIncomplete &&
                        <WorkableIconErrorButton
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(true);
                                const incompleteEntry = entriesFilteres.find(x => !x.complete);
                                if (incompleteEntry) {
                                    setTimeout((identity) => scrollTo(`#${reportStep}-${identity}`, 200), 100, incompleteEntry.identity)
                                }
                            }}
                            tooltip={Tr("annual_report_section_incomplete")}
                            className={classes.sectionErrorIcon}
                        />
                    }
                </AnnualReportTableCell>
                <AnnualReportTableCell borderLeft>{entriesCounted.count}</AnnualReportTableCell>
                <AnnualReportTableCell borderLeft>{formatAmountNumber(entriesCounted.contractValue) + " zł"}</AnnualReportTableCell>

                <AnnualReportTableCell borderLeft borderRight>
                    <WorkableIconExpandButton expanded={expanded} onClick={() => setExpanded(!expanded)}/>
                </AnnualReportTableCell>
            </TableRow>
            {expanded &&
                <TableRow>
                    <AnnualReportTableCell colSpan={6} borderLeft borderRight className={classes.demandsCell}>
                        <Table className={classes.demandsTable}>
                            <TableHead>
                                <TableRow>
                                    <AnnualReportTableHeadCell borderLeft={false} label={`mmia.${reportStep}.number`}/>
                                    <AnnualReportTableHeadCell borderLeft={false} label={`mmia.${reportStep}.name`}/>
                                    <AnnualReportTableHeadCell label={`mmia.${reportStep}.contractValue`}/>
                                    <TableCell className={classes.buttonColumn}></TableCell>
                                    <TableCell className={classes.buttonColumn}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entriesFilteres.map((entry, index) => (
                                    <TableRow id={`${reportStep}-${entry.identity}`} key={index}
                                              className={clsx(classes.demandsRow, {complete: entry.complete, incomplete: !entry.complete})}>
                                        <AnnualReportTableCell>
                                            {entry.number}
                                        </AnnualReportTableCell>
                                        <AnnualReportTableCell>
                                            {entry.name}
                                        </AnnualReportTableCell>
                                        <AnnualReportTableCell>
                                            {formatAmountNumber(entry.contractValue)}
                                        </AnnualReportTableCell>
                                        <AnnualReportTableCell className={classes.buttonColumn}>
                                            <WorkableIconEditButton disabled={disabled} onClick={() => setEditPopup(getAnnualReportEditPopup(entry))}/>
                                            {
                                                entry.complete ?
                                                    <IconButton tooltip={Tr("mark_draft_version")} onClick={() => changeComplete(entry, false)}
                                                                Icon={CheckCircleOutlineIcon} disabled={disabled}/>
                                                    :
                                                    <IconButton tooltip={Tr("mark_saved_version")} onClick={() => changeComplete(entry, true)}
                                                                disabled={disabled} Icon={RadioButtonUncheckedIcon}/>
                                            }
                                        </AnnualReportTableCell>
                                        <AnnualReportTableCell className={classes.buttonColumn}>
                                            <WorkableIconButton
                                                tooltip={Tr("change_exclusion")} Icon={SwapVerticalCircleIcon} disabled={disabled}
                                                onClick={() => setMovePopup(getMoveAnnualReportEntryConfig(params.id, entry.identity, type))}/>
                                            <WorkableIconDeleteButton disabled={disabled} onClick={() => doDelete(entry)}
                                            />
                                        </AnnualReportTableCell>
                                    </TableRow>
                                ))}

                                {!entriesFilteres.length && <TableRow>
                                    <AnnualReportTableCell className={classes.noOptions} colSpan={5}>
                                        {Tr("no_options")}
                                    </AnnualReportTableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </AnnualReportTableCell>
                </TableRow>
            }

        </>
    );
}

AnnualReportExceptionTablePart.propTypes = {
    type: PropTypes.string.isRequired,
    reportStep: PropTypes.string.isRequired,
    classes: PropTypes.object.isRequired,
    disabled: PropTypes.bool.isRequired,
    entries: PropTypes.array.isRequired,
    search: PropTypes.shape({
        search: PropTypes.string
    }).isRequired,
    setEditPopup: PropTypes.func.isRequired,
    setMovePopup: PropTypes.func.isRequired,
}

export default AnnualReportExceptionTablePart;